/**
 * StarButton Component
 * Toggle button for starring/unstarring studies
 */

import React, { useState } from 'react';
import { Star } from 'lucide-react';

interface StarButtonProps {
  isStarred: boolean;
  onToggle: () => Promise<void>;
  size?: 'sm' | 'md' | 'lg';
}

export function StarButton({ isStarred, onToggle, size = 'md' }: StarButtonProps) {
  const [loading, setLoading] = useState(false);

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (loading) return;

    setLoading(true);
    try {
      await onToggle();
    } catch (error) {
      console.error('Error toggling star:', error);
    } finally {
      setLoading(false);
    }
  };

  const sizeClasses = {
    sm: 'p-1',
    md: 'p-1.5',
    lg: 'p-2',
  };

  const iconSizes = {
    sm: 14,
    md: 18,
    lg: 22,
  };

  return (
    <button
      onClick={handleClick}
      disabled={loading}
      className={`
        ${sizeClasses[size]}
        rounded-full
        transition-all
        duration-150
        focus:outline-none focus:ring-2 focus:ring-primary-500
        ${isStarred
          ? 'text-yellow-500 hover:text-yellow-600 hover:bg-yellow-50 dark:hover:text-yellow-400 dark:hover:bg-yellow-900/20'
          : 'text-gray-400 hover:text-yellow-500 hover:bg-gray-50 dark:text-gray-500 dark:hover:text-yellow-400 dark:hover:bg-gray-700'
        }
        ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
      title={isStarred ? 'Remove from My Studies' : 'Add to My Studies'}
    >
      <Star
        size={iconSizes[size]}
        fill={isStarred ? 'currentColor' : 'none'}
        strokeWidth={2}
      />
    </button>
  );
}

export default StarButton;
